import { useEffect, useMemo, useState } from "react";
import MuiDialog from "@mui/material/Dialog";
import { IconAlertTriangle, IconX } from "@tabler/icons-react";
import { Button } from "../../../../components/common/button";
import { Heading, Text } from "../../../../components/common/typography";
import { Input } from "../../../../components/common/input";
import { Select } from "../../../../components/forms/select";
import {
  createMyWarrantyProduct,
  updateMyWarrantyProduct,
  type WarrantyProductSummary,
} from "../../hooks/useWarrantyProducts";
import { useProductWarranties } from "../../hooks/useProductWarranties";
import { useProductWarrantyTypes } from "../../hooks/useProductWarrantyTypes";
import { useProductWarrantyProviders } from "../../hooks/useProductWarrantyProviders";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import {
  validateWarrantyForm,
  WARRANTY_DESCRIPTION_MAX_LENGTH,
  WARRANTY_NAME_MAX_LENGTH,
  type WarrantyFormErrors,
  type WarrantyFormValues,
} from "../../utils/warranty-form-validation";

interface Props {
  open: boolean;
  /** When set, the dialog edits this row instead of creating a new one. */
  initial?: WarrantyProductSummary | null;
  onClose: () => void;
  onSaved: (saved: WarrantyProductSummary) => void;
}

const EMPTY_VALUES: WarrantyFormValues = {
  name: "",
  price: "",
  warrantyId: "",
  warrantyTypeId: "",
  warrantyProviderId: "",
  description: "",
};

function toFormValues(w: WarrantyProductSummary): WarrantyFormValues {
  return {
    name: w.name ?? "",
    price: w.price != null ? String(w.price) : "",
    warrantyId: w.warrantyId != null ? String(w.warrantyId) : "",
    warrantyTypeId: w.warrantyTypeId != null ? String(w.warrantyTypeId) : "",
    warrantyProviderId: w.warrantyProviderId != null ? String(w.warrantyProviderId) : "",
    description: w.description ?? "",
  };
}

/**
 * Shared create/edit dialog for seller-owned warranty SKUs. Used inline on
 * /my-products/new (Compatible warranties section) and on the dedicated
 * /my-warranties management page.
 */
export function WarrantyFormDialog({ open, initial, onClose, onSaved }: Props) {
  const isEdit = initial != null;
  const { data: warranties, error: warrantiesError } = useProductWarranties();
  const { data: warrantyTypes } = useProductWarrantyTypes();
  const { data: warrantyProviders } = useProductWarrantyProviders();

  const periodOptions = useMemo(
    () => (warranties ?? []).map((w) => ({ value: String(w.id), label: w.name })),
    [warranties],
  );
  const typeOptions = useMemo(
    () => (warrantyTypes ?? []).map((t) => ({ value: String(t.id), label: t.name })),
    [warrantyTypes],
  );
  const providerOptions = useMemo(
    () => (warrantyProviders ?? []).map((p) => ({ value: String(p.id), label: p.name })),
    [warrantyProviders],
  );

  const [values, setValues] = useState<WarrantyFormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<WarrantyFormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  // Re-seed the form whenever the dialog opens (or the row being edited changes).
  useEffect(() => {
    if (!open) return;
    setValues(initial ? toFormValues(initial) : EMPTY_VALUES);
    setErrors({});
  }, [open, initial]);

  function setField(field: keyof WarrantyFormValues, value: string) {
    setValues((prev) => ({ ...prev, [field]: value }));
    // Drop the field's message as soon as the seller edits it; the next submit re-checks.
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }

  const isDebug = import.meta.env.DEV;
  function handleDevAutofill() {
    const period = (warranties ?? [])[0];
    const type = (warrantyTypes ?? [])[0];
    const provider = (warrantyProviders ?? [])[0];
    const periodLabel = period?.name ?? "Extended";
    setValues({
      name: `${periodLabel} debug warranty ${Math.floor(Math.random() * 9000 + 1000)}`,
      price: String(Math.floor(Math.random() * 900 + 100)),
      warrantyId: period ? String(period.id) : "",
      warrantyTypeId: type ? String(type.id) : "",
      warrantyProviderId: provider ? String(provider.id) : "",
      description:
        "DEV autofill. Covers screen + battery for the included period; advance replacement.",
    });
    setErrors({});
  }

  async function handleSubmit() {
    const nextErrors = validateWarrantyForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      const payload = {
        name: values.name.trim(),
        price: Number(values.price),
        warrantyId: Number(values.warrantyId),
        warrantyTypeId: Number(values.warrantyTypeId),
        warrantyProviderId: Number(values.warrantyProviderId),
        description: values.description.trim() || undefined,
      };
      const saved = isEdit
        ? await updateMyWarrantyProduct(initial!.id, payload)
        : await createMyWarrantyProduct(payload);
      showSuccessToast(isEdit ? "Warranty updated" : "Warranty created");
      onSaved(saved);
      onClose();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
          ?.message;
      const flat = Array.isArray(msg) ? msg.join(", ") : msg;
      showErrorToast(flat || "Failed to save warranty");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: {
          className: "!rounded-2xl !max-w-xl !shadow-xl",
          sx: {
            backgroundColor: "var(--color-bg-default)",
            backgroundImage: "none",
          },
        },
      }}
    >
      <div className="flex items-start justify-between border-b border-borderDefault px-6 py-4">
        <Heading variant="h3" tone="dark">
          {isEdit ? "Edit warranty" : "New warranty product"}
        </Heading>
        <div className="flex items-center gap-2">
          {isDebug && !isEdit && (
            <Button variant="secondary" size="sm" type="button" onClick={handleDevAutofill}>
              DEV - Autofill
            </Button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-textMuted hover:text-textDark transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-4 px-6 py-5">
        {isEdit && (initial?.orderUsageCount ?? 0) > 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-borderDefault bg-bgLight p-3">
            <IconAlertTriangle size={18} className="mt-0.5 shrink-0 text-textDark" />
            <Text variant="bodySm" tone="dark">
              This warranty has been sold {initial!.orderUsageCount}{" "}
              {(initial!.orderUsageCount ?? 0) === 1 ? "time" : "times"}. Past
              orders keep their original price and terms — changes here only
              affect new purchases. Edit period, type, or provider with care:
              they describe what the warranty IS to anyone reading the order.
            </Text>
          </div>
        )}
        <Input
          label="Name"
          required
          value={values.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder="e.g. Premium Warranty 3 Years"
          maxLength={WARRANTY_NAME_MAX_LENGTH}
          error={errors.name}
        />
        <Input
          label="Price (QAR)"
          required
          inputMode="decimal"
          value={values.price}
          onChange={(e) => setField("price", e.target.value)}
          placeholder="e.g. 650"
          error={errors.price}
        />
        <div className="grid grid-cols-3 gap-3">
          <Select
            name="warrantyId"
            label="Period"
            required
            value={values.warrantyId}
            onChange={(e) => setField("warrantyId", e.target.value)}
            options={periodOptions}
            placeholder="Select period"
            error={errors.warrantyId}
            helperText={warrantiesError ? "Periods couldn't be loaded. Try again later." : undefined}
          />
          <Select
            name="warrantyTypeId"
            label="Type"
            required
            value={values.warrantyTypeId}
            onChange={(e) => setField("warrantyTypeId", e.target.value)}
            options={typeOptions}
            placeholder="Select type"
            error={errors.warrantyTypeId}
          />
          <Select
            name="warrantyProviderId"
            label="Provider"
            required
            value={values.warrantyProviderId}
            onChange={(e) => setField("warrantyProviderId", e.target.value)}
            options={providerOptions}
            placeholder="Select provider"
            error={errors.warrantyProviderId}
          />
        </div>
        <Input
          label="Description"
          value={values.description}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="Buyer-facing terms summary"
          maxLength={WARRANTY_DESCRIPTION_MAX_LENGTH}
          error={errors.description}
        />
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-borderDefault px-6 py-4">
        <Button variant="secondary" size="md" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="filledDark" size="md" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving…" : isEdit ? "Save changes" : "Create"}
        </Button>
      </div>
    </MuiDialog>
  );
}
